import React, { useCallback, useEffect, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { Moon, Sunrise, Sun, Sunset, CloudMoon, RefreshCw, AlertCircle, MapPin } from "lucide-react";

const API = "https://api.aladhan.com/v1/timingsByCity";

// Prayer rows (Sunrise is shown for reference but not treated as a salah for the "next" highlight)
const ROWS = [
  { key: "Fajr", labelKey: "prayer.fajr", icon: CloudMoon, salah: true },
  { key: "Sunrise", labelKey: "prayer.sunrise", icon: Sunrise, salah: false },
  { key: "Dhuhr", labelKey: "prayer.dhuhr", icon: Sun, salah: true },
  { key: "Asr", labelKey: "prayer.asr", icon: Sun, salah: true },
  { key: "Maghrib", labelKey: "prayer.maghrib", icon: Sunset, salah: true },
  { key: "Isha", labelKey: "prayer.isha", icon: Moon, salah: true },
];

function saudiParts() {
  const p = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Riyadh", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(new Date());
  const g = (t) => p.find((x) => x.type === t).value;
  return { date: `${g("day")}-${g("month")}-${g("year")}`, minutes: parseInt(g("hour"), 10) * 60 + parseInt(g("minute"), 10) };
}

// hh:mm (may include " (EEST)") -> minutes
const toMin = (s) => {
  const m = String(s).match(/(\d{1,2}):(\d{2})/);
  return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
};
const fmt12 = (s) => {
  const m = toMin(s);
  if (m == null) return s;
  let h = Math.floor(m / 60);
  const mm = String(m % 60).padStart(2, "0");
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${mm} ${ap}`;
};

async function fetchTimings(city) {
  const { date } = saudiParts();
  const url = `${API}/${date}?city=${city}&country=Saudi%20Arabia&method=4`;
  const res = await fetch(url, { headers: { Accept: "application/json" }, signal: AbortSignal.timeout(10000) });
  if (!res.ok) throw new Error(String(res.status));
  const json = await res.json();
  const t = json?.data?.timings;
  if (!t) throw new Error("shape");
  return { Fajr: t.Fajr, Sunrise: t.Sunrise, Dhuhr: t.Dhuhr, Asr: t.Asr, Maghrib: t.Maghrib, Isha: t.Isha };
}

// Decide default city from journey stage: Makkah portion begins ~ day 6 (trip start + 5 days)
function defaultCity(settings) {
  try {
    const start = settings?.tripStartDate ? new Date(settings.tripStartDate + "T00:00:00") : null;
    if (!start) return "Madinah";
    const makkah = new Date(start);
    makkah.setDate(makkah.getDate() + 5);
    return new Date() >= makkah ? "Makkah" : "Madinah";
  } catch {
    return "Madinah";
  }
}

export const PrayerTimes = ({ settings }) => {
  const { t } = useLang();
  const [city, setCity] = useState(() => defaultCity(settings));
  const [timings, setTimings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [updated, setUpdated] = useState(null);
  const [nowMin, setNowMin] = useState(() => saudiParts().minutes);

  const load = useCallback(async (c) => {
    setLoading(true);
    setError(false);
    try {
      const data = await fetchTimings(c);
      setTimings(data);
      setUpdated(new Date());
    } catch {
      setTimings(null);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(city); }, [city, load]);

  // tick every 30s for countdown / next-salah advance
  useEffect(() => {
    const id = setInterval(() => setNowMin(saudiParts().minutes), 30000);
    return () => clearInterval(id);
  }, []);

  // next upcoming salah
  let nextKey = null, countdown = null;
  if (timings) {
    const salahs = ROWS.filter((r) => r.salah).map((r) => ({ key: r.key, min: toMin(timings[r.key]) }));
    let next = salahs.find((s) => s.min != null && s.min > nowMin);
    let diff;
    if (next) {
      diff = next.min - nowMin;
    } else {
      next = salahs[0]; // tomorrow's Fajr
      diff = 24 * 60 - nowMin + (next?.min ?? 0);
    }
    if (next) {
      nextKey = next.key;
      const h = Math.floor(diff / 60), m = diff % 60;
      countdown = h > 0 ? `${h}h ${m}m` : `${m}m`;
    }
  }

  const cityTitle = city === "Makkah" ? t("prayer.makkahTitle") : t("prayer.madinahTitle");

  return (
    <section className="animate-fade-up" data-testid="prayer-times-card">
      <div className="maqam-card overflow-hidden p-0">
        {/* header */}
        <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-5 py-4">
          <div className="flex items-center gap-2.5">
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-[#0A192F] text-[#E9CE7D]">
              <Moon size={17} strokeWidth={1.8} />
            </span>
            <div>
              <h2 className="font-display text-base font-bold leading-tight text-[#0A192F]" data-testid="prayer-city-title">{cityTitle}</h2>
              <p className="text-[11px] text-slate-400">{t("prayer.method")}</p>
            </div>
          </div>
          {/* city switcher */}
          <div className="flex rounded-full bg-slate-100 p-0.5" data-testid="prayer-city-switch">
            {["Madinah", "Makkah"].map((c) => (
              <button
                key={c}
                onClick={() => setCity(c)}
                data-testid={`prayer-city-${c.toLowerCase()}`}
                className={`rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${city === c ? "bg-[#0A192F] text-white" : "text-slate-500"}`}
              >
                {c === "Makkah" ? t("prayer.makkah") : t("prayer.madinah")}
              </button>
            ))}
          </div>
        </div>

        {/* body */}
        {error ? (
          <div className="px-5 py-8 text-center" data-testid="prayer-error">
            <AlertCircle size={22} className="mx-auto mb-2 text-[#B42318]" />
            <p className="mx-auto max-w-xs text-sm text-slate-500">{t("prayer.error")}</p>
            <button
              onClick={() => load(city)}
              data-testid="prayer-retry"
              className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#0A192F] px-4 py-2 text-xs font-bold text-white active:scale-95"
            >
              <RefreshCw size={13} /> {t("prayer.retry")}
            </button>
          </div>
        ) : loading ? (
          <div className="space-y-2 p-5">
            {ROWS.map((r) => (
              <div key={r.key} className="h-11 animate-pulse rounded-xl bg-slate-100" />
            ))}
          </div>
        ) : (
          <>
            {/* next salah highlight */}
            {nextKey && (
              <div className="mx-4 mt-4 flex items-center justify-between rounded-2xl bg-[#EAF2FC] px-4 py-3" data-testid="prayer-next">
                <span className="text-sm font-semibold text-[#3E6B9A]">
                  {t("prayer.next")}: {t(`prayer.${nextKey.toLowerCase()}`)}
                </span>
                <span className="font-display text-sm font-bold text-[#0A192F]">
                  {t("prayer.in")} {countdown}
                </span>
              </div>
            )}
            <div className="p-4">
              {ROWS.map(({ key, labelKey, icon: Icon }) => {
                const active = key === nextKey;
                return (
                  <div
                    key={key}
                    data-testid={`prayer-row-${key.toLowerCase()}`}
                    className={`flex items-center justify-between rounded-xl px-3 py-2.5 ${active ? "bg-[#0A192F]" : ""}`}
                  >
                    <span className={`flex items-center gap-2.5 text-sm font-semibold ${active ? "text-white" : "text-[#0A192F]"}`}>
                      <Icon size={16} className={active ? "text-[#E9CE7D]" : "text-[#B9D0EC]"} />
                      {t(labelKey)}
                    </span>
                    <span className={`font-display text-base font-bold ${active ? "text-[#E9CE7D]" : "text-[#0A192F]"}`}>
                      {fmt12(timings[key])}
                    </span>
                  </div>
                );
              })}
            </div>
            {updated && (
              <div className="flex items-center justify-between gap-2 border-t border-slate-100 px-5 py-2.5">
                <span className="flex items-center gap-1.5 text-[11px] text-slate-400">
                  <MapPin size={11} /> {t("prayer.updated")}: {updated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
                <button onClick={() => load(city)} data-testid="prayer-refresh" className="text-slate-400 active:scale-90">
                  <RefreshCw size={14} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};
